"use strict";
/**
 * @packageDocumentation
 * @module SubscriptionPromise
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeSubscriptionPromiseMulti = exports.makeSubscriptionPromise = void 0;
const utils_1 = require("@kiltprotocol/utils");
/**
 * Helps building a promise associated to a subscription callback through which updates can be pushed to the promise.
 * This promise is resolved with the value of latest update when a resolution criterion is met.
 * It is rejected with a custom error/reason if a rejection criterion is met or on timeout (optional). Rejection takes precedent.
 *
 * @param terminationOptions .
 * @param terminationOptions.resolveOn Resolution criterion. A function that evaluates an incoming update from the subscription.
 * If it returns a truthy value, the promise is resolved with the value of the latest update.
 * @param terminationOptions.rejectOn Rejection criterion. A function that evaluates an incoming update from the subscription.
 * If it returns a truthy value, this value is used as rejection reason.
 * @param terminationOptions.timeout Timeout in ms. If set, the promise will reject if not resolved before the time is up.
 * @returns An object containing both a subscription callback
 * and a promise which resolves or rejects depending on the values pushed to the callback.
 */
function makeSubscriptionPromise(terminationOptions) {
    const { resolveOn, rejectOn, timeout } = Object.assign({}, terminationOptions);
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    const subscription = typeof rejectOn === 'function'
        ? (value) => {
            const rejectedReason = rejectOn(value);
            if (rejectedReason)
                reject(rejectedReason);
            const resolved = resolveOn(value);
            if (resolved)
                resolve(value);
        }
        : (value) => {
            const resolved = resolveOn(value);
            if (resolved)
                resolve(value);
        };
    if (timeout)
        setTimeout(() => {
            reject(utils_1.SDKErrors.ERROR_TIMEOUT());
        }, timeout);
    return { promise, subscription };
}
exports.makeSubscriptionPromise = makeSubscriptionPromise;
/**
 * A wrapper around [[makeSubscriptionPromise]] that helps building multiple promises which listen to the same subscription.
 *
 * @param args An array of objects each of which provides the arguments for creation of one promise.
 * @returns An object containing both a subscription callback
 * and an array of promises which resolve or reject depending on the values pushed to the callback.
 */
function makeSubscriptionPromiseMulti(args) {
    const promises = [];
    const subscriptions = [];
    args.forEach((options) => {
        const { promise, subscription } = makeSubscriptionPromise(options);
        promises.push(promise);
        subscriptions.push(subscription);
    });
    const subscription = (value) => {
        subscriptions.forEach((s) => s(value));
    };
    return { promises, subscription };
}
exports.makeSubscriptionPromiseMulti = makeSubscriptionPromiseMulti;
